<template>
  <div class="px-0 md:px-[55px] text-left w-full">
    
    <div class="mb-6">
      <h2 class="text-[#FFFFFF] text-m font-bold mb-4">Effective Date: 11/03/2025</h2>
      
      <p class="leading-relaxed description font-regular mb-2">
        Welcome to Sequreio (“Company,” “we,” “our,” or “us”).
      </p>

      <p class="leading-relaxed description font-regular mb-2">
        These Terms and Conditions (“Terms”) govern your use of our password generation and management application (the “App” or “Services”).
      </p>

      <p class="leading-relaxed description font-regular">
        By downloading, accessing, or using our password generator app, you agree to these Terms of Use.
      </p>
    </div>

    <div class="space-y-5 text-[#FFFFFF]">
      
      <h3 class="font-bold text-lg">1. Acceptance of Terms</h3>
      <p class="leading-relaxed description font-regular mb-2">
        By using our app, you acknowledge that you have read, understood, 
        and agree to be bound by these Terms of Use. If you do not agree, 
        do not use the app.
      </p>
      <p class="leading-relaxed description font-regular">
        These Terms are concluded between you and Sequreio only, and not with Apple Inc. Apple is not a party to these Terms and is not responsible for the App or its content. Sequreio, not Apple, is solely responsible for the App and its services.
      </p>

      <h3 class="font-bold text-lg">2. Eligibility</h3>
      <p class="leading-relaxed description font-regular mb-2">
        By using Sequreio, you confirm that:
      </p>
      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2">You are at least 13 years of age or the age of majority in your jurisdiction.</li>
        <li class="mb-2">You have the legal authority to enter into this agreement.</li>
        <li class="mb-2">If you are using the App on behalf of a company or organisation, you are authorised to bind them to these Terms.</li>
        <li class="mb-2">You may use this app solely for personal, non-commercial purposes.</li>
        <li class="mb-2">You are responsible for maintaining the security of your generated passwords.</li>
      </ul>

      <h3 class="font-bold text-lg">3. Maintenance and Support</h3>
      <p class="leading-relaxed description font-regular mb-6">
        Sequreio is solely responsible for providing any maintenance and support services for the App, as specified in these Terms or as required under applicable law. Apple has no obligation whatsoever to furnish any maintenance or support services with respect to the App.
      </p>

      <h3 class="font-bold text-lg">4. User Accounts</h3>
      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Registration:</strong> You may be required to create an account by providing accurate and complete information.</li>
        <li class="mb-2"><strong>Security:</strong> You are responsible for maintaining the confidentiality of your login credentials.</li>
        <li class="mb-2"><strong>Unauthorised Access:</strong> Notify us immediately at <strong>pearcooperation@gmail.com</strong> if you suspect unauthorised access.</li>
        <li class="mb-2"><strong>Termination:</strong> We may suspend or terminate accounts that violate these Terms.</li>
      </ul>

      <h3 class="font-bold text-lg">5. Acceptable Use</h3>
      <p class="leading-relaxed description font-regular mb-2">
        You agree not to:
      </p>
      <ul class="list-disc list-inside ml-1 mb-4 leading-relaxed description font-regular">
        <li class="mb-2">Use the App for unlawful purposes.</li>
        <li class="mb-2">Attempt to reverse-engineer, hack, or interfere with the App’s functionality.</li>
        <li class="mb-2">Share or distribute generated passwords with unauthorised parties.</li>
        <li class="mb-2">Transmit harmful content such as malware or viruses.</li>
      </ul>

      <p class="leading-relaxed description font-regular mb-6">
        Violation of these rules may result in <strong>immediate suspension</strong> or <strong>termination</strong> of your account.
      </p>

      <h3 class="font-bold text-lg">6. Subscription Services, Payments, and Refunds</h3>

      <p class="leading-relaxed description font-regular mb-4">
        Sequreio offers both Free and Premium subscription tiers. Premium subscriptions provide access to advanced features including Trusted Devices, Breach Alerts, and Cross-Device Synchronisation.
      </p>

      <p class="leading-relaxed description mb-2 font-semibold">
        A. Subscription Tiers and Features
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Free Tier:</strong> Access to password generation, storage (up to 25 passwords), and organisation (up to 3 folders).</li>
        <li class="mb-2"><strong>Premium Tier:</strong> Unlimited passwords and folders, Trusted Device management (up to 5 devices), Cross-Device Sync, and priority support.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        B. Payment Processing
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Third-Party Processing:</strong> All subscriptions are processed through Apple's App Store. Payment is governed by Apple's Terms and Conditions.</li>
        <li class="mb-2"><strong>Subscription Plans:</strong> Available as monthly or annual subscriptions at prices displayed in the App Store for your region.</li>
        <li class="mb-2"><strong>Currency and Tax:</strong> Prices are displayed in your local currency and may include applicable taxes as required by law.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        C. Auto-Renewal and Cancellation
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Automatic Renewal:</strong> Subscriptions automatically renew at the end of each billing period unless cancelled at least 24 hours before renewal.</li>
        <li class="mb-2"><strong>Cancellation Process:</strong> Cancel anytime through your Apple App Store account settings. Cancellation takes effect at the end of the current billing period.</li>
        <li class="mb-2"><strong>No Partial Refunds:</strong> Cancelling a subscription does not entitle you to a refund for the remaining portion of the current billing period.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        D. Refund Policy
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Apple Refund Policy:</strong> All refund requests must be submitted directly to Apple through the App Store within 14 days of purchase, subject to Apple's refund policies.</li>
        <li class="mb-2"><strong>Eligibility:</strong> Refunds are granted at Apple's sole discretion and typically only for technical issues, billing errors, or accidental purchases.</li>
        <li class="mb-2"><strong>Non-Refundable Circumstances:</strong> Refunds are not available for subscription renewals unless cancelled before the renewal date, or for partial subscription periods.</li>
        <li class="mb-2"><strong>Contact:</strong> For refund assistance, contact Apple Support or reach us at <strong>pearcooperation@gmail.com</strong> for guidance.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        E. Subscription Expiration and Service Degradation
      </p>

      <p class="leading-relaxed description mb-2 font-regular">
        When your Premium subscription expires (due to non-renewal, cancellation, or failed payment):
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Immediate Feature Restriction:</strong> Premium features (Trusted Devices, Cross-Device Sync) become inaccessible immediately upon expiration.</li>
        <li class="mb-2"><strong>Data Retention:</strong> Your passwords, folders, and account data remain securely stored and encrypted. No data is deleted.</li>
        <li class="mb-2"><strong>Free Tier Limits:</strong> If you exceed Free tier limits (25 passwords, 3 folders), you can view but not create new items until limits are met or subscription is renewed.</li>
        <li class="mb-2"><strong>Trusted Devices:</strong> Previously registered trusted devices may be deactivated after 90 days of subscription lapse for security purposes.</li>
        <li class="mb-2"><strong>Restoration Upon Renewal:</strong> All Premium features and device registrations are immediately restored when you renew your subscription.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        F. Price Changes
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2">We reserve the right to modify subscription pricing. Existing subscribers will be notified at least 30 days before price changes take effect.</li>
        <li class="mb-2">Price changes apply to new subscriptions and renewals after the effective date.</li>
      </ul>

      <p class="leading-relaxed description mb-2 font-semibold">
        G. Free Trials and Promotional Offers
      </p>

      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Trial Eligibility:</strong> Free trials (if offered) are available once per user and require valid payment information.</li>
        <li class="mb-2"><strong>Trial Cancellation:</strong> Cancel before the trial period ends to avoid charges.</li>
        <li class="mb-2"><strong>Promotional Terms:</strong> Special offers are subject to specific terms displayed at the time of purchase.</li>
      </ul>

      <h3 class="font-bold text-lg">7. Intellectual Property</h3>
      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Ownership:</strong> All content, trademarks, logos, and intellectual property within the App belong to <strong>Sequreio.</strong></li>
        <li class="mb-2"><strong>License:</strong> We grant you a non-exclusive, non-transferable, limited license to use the App for personal purposes.</li>
        <li class="mb-2"><strong>Restrictions:</strong> You may not modify, reproduce, or exploit the App's content without prior written consent.</li>
        <li class="mb-2"><strong>Third-Party IP Claims:</strong> In the event of any third-party claim that the App or your possession and use of it infringes that third party's intellectual property rights, Sequreio — not Apple — will be solely responsible for the investigation, defence, settlement, and discharge of any such claim.</li>
      </ul>

      <h3 class="font-bold text-lg">8. Privacy Policy</h3>
      <p class="leading-relaxed description font-regular mb-6">
        Your use of the App is subject to our Privacy Policy, which explains how we collect, use, and protect your data. 
        We carefully recommend you to check the Privacy Policy Page.
      </p>

      <h3 class="font-bold text-lg">9. Limitation of Liability</h3>
      <p class="leading-relaxed description font-regular mb-2">
        To the fullest extent permitted by law:
      </p>
      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2"><strong>Limited Liability:</strong> We are not liable for indirect, incidental, or consequential damages resulting from your use of the App.</li>
        <li class="mb-2"><strong>Security Disclaimer:</strong> While we implement encryption and security measures, we cannot guarantee 100% protection against breaches.</li>
      </ul>

      <h3 class="font-bold text-lg">10. Termination</h3>
      <p class="leading-relaxed description font-regular mb-2">
        We reserve the right to:
      </p>
      <ul class="list-disc list-inside ml-1 mb-6 leading-relaxed description font-regular">
        <li class="mb-2">Suspend or terminate your access to the App at any time without notice if you violate these terms.</li>
        <li class="mb-2">Disable inactive accounts after 12 months of inactivity.</li>
      </ul>

      <h3 class="font-bold text-lg">11. Updates and Changes</h3>
      <p class="leading-relaxed description font-regular mb-2">
        We may update these Terms periodically. Major changes will be communicated through:
      </p>
      <ul class="list-disc list-inside ml-1 mb-4 leading-relaxed description font-regular">
        <li class="mb-2">In-App Notifications.</li>
        <li class="mb-2">Email Alerts.</li>
        <li class="mb-2">Policy Update Banners.</li>
      </ul>
      <p class="leading-relaxed description font-regular mb-6">
        Continued use of the App after changes indicates your acceptance of the revised Terms.
      </p>

      <h3 class="font-bold text-lg">12. Warranty Disclaimer</h3>
      <p class="leading-relaxed description font-regular mb-2">
        The App is provided on an <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> basis without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
      </p>
      <p class="leading-relaxed description font-regular mb-6">
        Sequreio does not warrant that: (a) the App will function uninterrupted, securely, or be available at any particular time or location; (b) any errors or defects will be corrected; (c) the App is free of viruses or other harmful components; or (d) the results of using the App will meet your requirements. Your use of the App is at your sole risk.
      </p>

      <h3 class="font-bold text-lg">13. Governing Law</h3>
      <p class="leading-relaxed description font-regular mb-2">
        These Terms shall be governed by and construed in accordance with the laws of Poland and, where applicable, the laws of the European Union, without regard to conflict of law principles.
      </p>
      <p class="leading-relaxed description font-regular mb-6">
        If any provision of these Terms is held to be invalid or unenforceable by a court of competent jurisdiction, the remaining provisions will remain in full force and effect. These Terms constitute the entire agreement between you and Sequreio regarding the App and supersede all prior agreements.
      </p>

      <h3 class="font-bold text-lg">14. Dispute Resolution</h3>
      <p class="leading-relaxed description font-regular mb-2">
        Any dispute, controversy, or claim arising out of or relating to these Terms or the use of the App shall first be attempted to be resolved through good-faith negotiation by contacting us at <strong>pearcooperation@gmail.com</strong>.
      </p>
      <p class="leading-relaxed description font-regular mb-6">
        If a resolution cannot be reached, disputes shall be subject to the exclusive jurisdiction of the competent courts of Poland. If you are a consumer residing in the European Union, you retain the right to bring proceedings before the courts of your country of residence and may also use the EU Online Dispute Resolution platform available at <a href="https://ec.europa.eu/consumers/odr" target="_blank" class="underline">ec.europa.eu/consumers/odr</a>.
      </p>

      <h3 class="font-bold text-lg">15. Contact Information</h3>
      <p class="leading-relaxed description font-regular mb-2">
        For any questions or disputes regarding these Terms, please contact us at:
      </p>
      <p class="leading-relaxed description font-regular mb-6">
        <strong>Pear Cooperation</strong><br>
        pearcooperation@gmail.com
      </p>

      <h3 class="font-bold text-lg">16. Third-Party Beneficiary</h3>
      <p class="leading-relaxed description font-regular">
        You acknowledge and agree that Apple Inc. and its subsidiaries are third-party beneficiaries of these Terms. Upon your acceptance of these Terms, Apple will have the right — and will be deemed to have accepted the right — to enforce these Terms against you as a third-party beneficiary thereof.
      </p>
    </div>
  </div>
</template>

<script setup>
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Albert+Sans:wght@300;400;500;600&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
</style>

<style scoped> 
.font-light {
  font-family: 'Albert Sans', sans-serif;
  font-weight: 300; /* Light */
}

.font-regular {
  font-family: 'Albert Sans', sans-serif;
  font-weight: 400; /* Regular */
}

.font-regular-inter {
  font-family: 'Inter', sans-serif;
  font-weight: 400; /* Regular */
}

.font-semibold {
  font-family: 'Albert Sans', sans-serif;
  font-weight: 500; /* Semibold */
}

.font-bold {
  font-family: 'Albert Sans', sans-serif;
  font-weight: 600; /* Bold */
}

.description {
    font-size: 14px; /* Smaller text */
    line-height: 24px;
    color: rgba(255, 255, 255, 0.7); /* Slightly faded white */
}
</style>